import { Link } from '@tanstack/react-router';
import type { FileRoutesByTo } from '@uturn/portal/routeTree.gen';
import type { ButtonProps, TabsVariant } from '@uturn/ui-kit';
import {
	Button,
	buttonVariants,
	cn,
	tabTriggerActiveClass,
} from '@uturn/ui-kit';
import type { ReactNode } from 'react';

const inactiveTabClass =
	'opacity-50 hover:bg-transparent hover:underline hover:opacity-100';
const defaultTabClass = 'justify-start px-4';

export function Tab(props: TabType & { variant?: TabsVariant }) {
	const { title, active, variant = 'primary' } = props;
	const activeTabClass = tabTriggerActiveClass(variant);
	if ('onClick' in props && props.onClick) {
		return (
			<Button
				variant="ghost"
				size="sm"
				onClick={props.onClick}
				className={cn(
					active ? activeTabClass : inactiveTabClass,
					defaultTabClass,
				)}
			>
				{title}
			</Button>
		);
	}
	return (
		<Link
			to={'to' in props ? props.to : ''}
			params={props.params}
			className={cn(
				buttonVariants({
					variant: 'ghost',
					size: 'sm',
				}),
				active ? activeTabClass : inactiveTabClass,
				defaultTabClass,
			)}
		>
			{title}
		</Link>
	);
}

type BasicTab = {
	title: ReactNode;
	active?: boolean;
};

export type TabLink = BasicTab & {
	to: keyof FileRoutesByTo;
	params?: Record<string, unknown>;
};
export type TabButton = BasicTab & {
	onClick: ButtonProps['onClick'];
};

export type TabType = TabLink | TabButton;

export function Tabs({ start, end }: { start?: TabType[]; end?: TabType[] }) {
	if (!start?.length && !end?.length) {
		return null;
	}

	return (
		<div className="flex flex-wrap justify-between gap-2">
			{!!start?.length && (
				<div className="flex w-fit flex-wrap gap-1">
					{start.map((tab, i) => (
						<Tab
							key={i}
							to={'to' in tab ? tab.to : undefined}
							params={'params' in tab ? tab.params : undefined}
							onClick={'onClick' in tab ? tab.onClick : undefined}
							title={tab.title}
							active={tab.active}
						/>
					))}
				</div>
			)}
			{!!end?.length && (
				<div className="ml-auto flex w-fit gap-1">
					{end.map((tab, i) => (
						<Tab
							key={i}
							variant="secondary"
							to={'to' in tab ? tab.to : undefined}
							params={'params' in tab ? tab.params : undefined}
							onClick={'onClick' in tab ? tab.onClick : undefined}
							title={tab.title}
							active={tab.active}
						/>
					))}
				</div>
			)}
		</div>
	);
}
